<template>
    <div class="execution-details">
        <div v-if="loading" class="text-center">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
        <div v-else class="row left-nav-collapsible">
            <div v-if="detailPageOpen" id="detail-page-open">
                <button class="btn details-open-btn" type="button" @click="onDetailPageOpenClose(false)">
                    <span> <i class="fa fa-caret-right"></i></span>
                </button>
            </div>
            <TransitionGroup :name="jobName ? 'slide' : ''" key="detail">
                <div v-if="showCloseBtn && !detailPageOpen" id="detail-page-close" key="close">
                    <button class="btn text-start details-open-btn" type="button" @click="onDetailPageOpenClose(true)">
                        <span> <i class="fa fa-caret-left"></i></span>
                    </button>
                </div>
                <div v-if="!detailPageOpen" id="details-col" @mouseover="onDetailPageHover(true)" key="details-col">
                    <div class="card">
                        <div class="card-body">
                            <div class="list-group list-group-flush my-n3">
                                <div class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Status</h5>
                                        </div>
                                        <div class="col-auto">
                                            <strong :class="[statusClass, 'text-uppercase']">{{ status }}</strong>
                                            <div v-if="status && status.toLowerCase() != 'failed' && status.toLowerCase() != 'succeeded' && status.toLowerCase() != 'cancelled' && count < 10"
                                                style="margin-left:10px"
                                                class="spinner-border spinner-border-sm text-primary">&nbsp;</div>
                                        </div>
                                    </div>
                                </div>
                                <div class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Job Name</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ jobName }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="cluster" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Cluster</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ cluster }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="gitCommit" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Commit (trunc)</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ gitCommit }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="submittedAt" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Submitted</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ submittedAt.format('MMM D, YYYY h:mm:ss A')
                                                }}</small>
                                            <span class="badge badge-secondary">{{ submittedRelativeTime }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="startedAt" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Started</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">
                                                {{ dayjs(startedAt).format('MMM D, YYYY h:mm:ssA') }}
                                            </small>
                                            <span class="badge badge-secondary">{{ startedAtRelativeTime }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="startedBy" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Started By</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ startedBy }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="completedAt" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Completed</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{
                                                dayjs(completedAt).format('MMM D, YYYY h:mm:ss A') }}</small><span
                                                class="badge badge-secondary">{{
                                                    convertToRelativeTime(dayjs(completedAt)) }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="queueTime" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Queue Time</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ convertToTime(queueTime) }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="runtime" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Runtime</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ convertToTime(runtime) }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="tag" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Tag</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ tag }}</small>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="failureReason" class="list-group-item">
                                    <div class="row align-items-center">
                                        <div class="col">
                                            <h5 class="mb-0">Failure Reason</h5>
                                        </div>
                                        <div class="col-auto">
                                            <small class="text-muted">{{ failureReason }}</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col" id="main-col" @mouseover="onDetailPageHover(false)" key="main-col">
                    <div class="row justify-content-end" v-if="enableRefreshBtn">
                        <div class="col-sm-3 d-flex position-absolute justify-content-end">
                            <button @click.prevent="onRefreshClick()" :disabled="refreshBtnLoading"
                                class="btn btn-sm btn-primary rounded-2 custom-refresh-btn">
                                <div v-if="refreshBtnLoading" class="spinner-border spinner-border-sm" role="status" />
                                Refresh
                            </button>
                        </div>
                    </div>
                    <tabs :tabs="tabItems" :btnTabs="true">
                        <template v-slot:logs>
                            <div class="row">
                                <div class="col-md-12" v-if="logOptions.length">
                                    <div class="card mb-n2 no-border">
                                        <div class="card-body">
                                            <input-field label="Select Log Type" type="select" :options="logOptions"
                                                v-model="selectedLogType" class="mb-0" />
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-12" v-if="selectedLogType == 'logs'">
                                    <div class="card no-border">

                                        <!-- <div class="card-header">
                                            <h4 class="card-header-title">
                                                Logs
                                            </h4>
                                            <div v-if="status && status.toLowerCase() != 'failed' && status.toLowerCase() != 'succeeded' && status.toLowerCase() != 'cancelled'"
                                                class="spinner-border spinner-border-sm float-end">&nbsp;</div>

                                        </div> -->
                                        <div class="card-body">
                                            <pre class="mb-0" v-if="logs" id="logs"><code>{{ logs }}</code></pre>
                                            <pre v-else><code>please wait...</code></pre>
                                            <pre class="mt-2 downloadLink "
                                                v-if="isDownloading"><code>Downloading...</code></pre>
                                            <div class="justify-content-center d-flex" v-if="enableShowMoreBtn">
                                                <button class="btn btn-sm btn-secondary mt-3 small-btn"
                                                    @click="onShowMoreClick" :disabled="showMoreBtnLoading">
                                                    <div v-if="showMoreBtnLoading"
                                                        class="spinner-border spinner-border-sm"
                                                        style="width: 1em; height: 1em;" role="status" />
                                                    Show more
                                                </button>
                                            </div>
                                            <div v-if="outputLogLink" class="p-2 downloadLink">
                                                <a :href="outputLogLink"
                                                    @click.prevent="downloadFile(outputLogLink, executionId + '_outputLog')"><span
                                                        class="fa fa-download"></span>&nbsp;Output Log</a>&nbsp; {{
                                                            outputLog }} MB
                                            </div>
                                            <div v-if="errorLogLink" class="p-2 mb-2 downloadLink">
                                                <a :href="errorLogLink"
                                                    @click.prevent="downloadFile(errorLogLink, executionId + '_errorLog')"><span
                                                        class="fa fa-download"></span>&nbsp;Error
                                                    Log&nbsp;&nbsp;&nbsp;&nbsp;</a> &nbsp;{{ errorLog }} MB
                                            </div>
                                        </div>
                                    </div>
                                    <div class="card"
                                        v-if="status && status.toLowerCase() == 'succeeded' && outputPaths.length">
                                        <div class="card-header">
                                            <h4 class="card-header-title">
                                                Output Paths
                                            </h4>
                                        </div>
                                        <div class="card-body">
                                            <ul>
                                                <li v-for="path in outputPaths" :key="path.index">{{ path }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-12" v-if="selectedLogType !== 'logs'">
                                    <div class="card no-border">
                                        <div class="card-body pt-0" id="logs">
                                            <JobExecutorLogs :sandboxName="sandboxId" :jobName="jobName"
                                                :executionId="executionId" :executorsId="selectedLogType"
                                                :jobStatus="status"></JobExecutorLogs>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-slot:logStreams>
                            <div class="card">
                                <div v-if="logStreams && logStreams.length">
                                    <div class="row justify-content-end">
                                        <div
                                            class="col-md-9 mt-4 me-4 d-flex align-items-center position-absolute justify-content-end gap-4">
                                            <div class="custom-control form-check form-switch">
                                                <input class="form-check-input" type="checkbox" role="switch"
                                                    id="AbbLogStreamSwitch" v-model="showLogDataInUnits">
                                                <label class="form-check-label" for="AbbLogStreamSwitch">Show
                                                    Abbreviated
                                                    Data</label>
                                            </div>
                                            <div v-if="status == 'running'">
                                                <button @click.prevent="getLogStreams()"
                                                    :disabled="getLogStreamsLoading" class="btn btn-sm btn-secondary">
                                                    <div v-if="getLogStreamsLoading"
                                                        class="spinner-border spinner-border-sm" role="status"
                                                        style="width: 1em; height: 1em;"></div>
                                                    <div v-else>Get Log Streams</div>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="card-body p-2">
                                        <datatable tableId="log-streams" :columns="logStreamColumns" :data="logStreams"
                                            :formatColumn="logStreamFormatColumn" :key="showLogDataInUnits"
                                            :compact="true" :showLoading="false" @viewModal="openModal"
                                            :viewModalColumn="viewModalColumn" />
                                    </div>
                                </div>
                                <div v-else class="card-body">
                                    <div class="d-flex justify-content-end" v-if="status == 'running'">
                                        <button @click.prevent="getLogStreams()" :disabled="getLogStreamsLoading"
                                            class="btn btn-sm btn-secondary">
                                            <div v-if="getLogStreamsLoading" class="spinner-border spinner-border-sm"
                                                role="status" style="width: 1em; height: 1em;"></div>
                                            <div v-else>Get Log Streams</div>
                                        </button>
                                    </div>
                                    <p>
                                    <pre>No log streams found!</pre>
                                    </p>
                                </div>
                            </div>
                        </template>
                        <template v-slot:[currentLogStreamId]>
                            <div class="card no-border">
                                <div class="card-body">
                                    <pre class="mb-0" id="logs"><code>{{ logStreamsData.Logs }}</code></pre>
                                </div>

                            </div>

                        </template>
                        <template v-slot:metrics>
                            <div class="card">
                                <div class="card-body">
                                    <div v-if="metricLoading" class="text-center mt-5">
                                        <div class="spinner-border" role="status">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                    <div v-else-if="countsV2Metrics && Object.keys(countsV2Metrics).length">
                                        <MetricsView :metricData="countsV2Metrics"
                                            :jobKind="isPromotion ? promotedJobKind : job.kind" :startedAt="startedAt"
                                            :completedAt="status == 'running' ? currentUtcTime : completedAt"
                                            :timeRange="timeRange" :aggInterval="aggInterval"
                                            @callGetCountV2Metrics="getCountV2Metrics"
                                            @onTimeRangeChange="timeRangeChange" @onIntervalChange="aggIntervalChange"
                                            :customStartTime="customStartTime" :customEndTime="customEndTime"
                                            :executionId="executionId" @apply-custom-range="handleCustomRange" />
                                    </div>
                                    <div v-else id="lake-view-metrics" class="row">
                                        <div class="col">
                                            <div class="row justify-content-end">
                                                <div v-if="metrics"
                                                    class="col-md-9 mt-4 d-flex position-absolute justify-content-end">
                                                    <div v-if="showMetricsFilterBtn"
                                                        class="custom-control form-check  me-5 form-switch">
                                                        <input class="form-check-input" type="checkbox" role="switch"
                                                            id="customSwitch1" v-model="showAllMetrics">
                                                        <label class="form-check-label" for="customSwitch1">All
                                                            Metrics</label>
                                                    </div>
                                                    <div class="custom-control form-check  me-5 form-switch">
                                                        <input class="form-check-input" type="checkbox" role="switch"
                                                            id="customSwitch2" v-model="showDataInUnits">
                                                        <label class="form-check-label" for="customSwitch2">Show
                                                            Abbreviated Data</label>
                                                    </div>
                                                </div>
                                                <div v-else class="card-body">
                                                    <p>
                                                    <pre>{{ metricsErrorMsg }}</pre>
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="card-body p-2" v-if="metrics && metrics.length">
                                                <datatable :tableId="executionId" :columns="metricColumns"
                                                    :data="filteredMetrics" :compact="true" :showLoading="false" />
                                            </div>


                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-slot:rerunHistory v-if="rerunHistory">
                            <div class="card">
                                <div class="">
                                    <datatable :tableId="`${executionId}-rerunHistory`" :columns="rerunHistoryColumns"
                                        :formatColumn="rerunHistoryFormatColumns" :orderColumn="rerunHistoryOrderColumn"
                                        :data="rerunHistory" :compact="true" :showLoading="false" :detailColumn="{
                                            renderBaseUrl: (data) => {
                                                return `${data.execution_id}`;
                                            },
                                        }" :highlightRow="(row) => row.execution_id === executionId"
                                        :highlightStyle="{ backgroundColor: 'var(--bs-light)', fontWeight: '600' }" />
                                </div>
                            </div>
                        </template>
                        <template v-slot:config v-if="Object.keys(yamlContent).length !== 0">
                            <div class="card">
                                <div class="card-body" id="execution-config-div">
                                    <job-editor v-on:tabChange="showTab" :editMode="false" v-model="yamlContent"
                                        :activeTab="activeTab"></job-editor>
                                </div>
                            </div>
                        </template>
                        <template v-slot:progress>
                            <div class="card">
                                <div class="card-body">
                                    <div class="d-flex align-items-center justify-content-between gap-2">
                                        <div class="d-flex align-items-center justify-content-start gap-2">
                                            <div v-if="pipelineView">
                                                <span class="badge bg-secondary steps-badge"
                                                    v-if="pipelineExecutionDetails?.total && pipelineExecutionDetails?.completed">
                                                    {{ pipelineExecutionDetails?.completed }}/{{
                                                        pipelineExecutionDetails?.total }} Jobs
                                                </span>
                                                <div>
                                                    <div
                                                        v-if="pipelineExecutionDetails?.status?.toLowerCase() == 'running' || pipelineExecutionDetails?.status?.toLowerCase() == 'submitted'">
                                                        <span class="spinner-border spinner-border-sm me-1"
                                                            role="status" style="width: 1em; height: 1em;">
                                                            <span class="visually-hidden">Loading...</span>
                                                        </span>
                                                        <strong
                                                            :class="[pipelineExecutionDetails?.status == 'RUNNING' ? 'text-primary' : 'text-warning']">{{
                                                                pipelineExecutionDetails?.status }}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="btn-group" role="group" style="margin-left: 8px;"
                                            aria-label="Basic radio toggle button group"
                                            v-if="job?.kind?.toLowerCase() === 'pipeline'">
                                            <button class="btn btn-sm"
                                                :class="!pipelineView ? 'btn-primary' : 'btn-default btn-light'"
                                                @click="togglePipelineView(false)">
                                                <i class="fa-solid fa-bars-progress"></i>
                                            </button>
                                            <button class="btn btn-sm"
                                                :class="pipelineView ? 'btn-primary' : 'btn-default btn-light'"
                                                @click="togglePipelineView(true)">
                                                <i class="fa-solid fa-diagram-project"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div v-if="pipelineView">
                                        <div v-if="loadingExecutionConfig" class="text-center mt-5">
                                            <div class="spinner-border spinner-border-sm me-1" role="status"
                                                style="width: 1em; height: 1em;">
                                                <span class="visually-hidden">Loading...</span>
                                            </div>
                                            Loading pipeline...
                                        </div>
                                        <div v-else>
                                            <div v-if="executionConfig" style="height: 600px; margin-top: 10px;">
                                                <pipeline-graph :pipelineJobConfig="pipelineJobConfig"
                                                    :jobs="pipelineJobsAll"
                                                    :key="`pipeline-${executionId}-${detailPageOpen}-${resetViewTrigger}`"
                                                    :pipelineExecutionDetails="pipelineExecutionDetails"
                                                    :countsMetrics="nodeMetricsInfo" @nodeClick="onNodeClick"
                                                    :showresetViewBtn="true" @resetView="resetViewTrigger++" />
                                            </div>
                                            <div v-else class="text-center mt-5">
                                                <p>No execution config found to display pipeline!</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else id="progress-config-div" style="margin-top: 10px;">
                                        <JobExecutionProgress :sandboxName="sandboxId" :jobName="jobName"
                                            :executionId="executionId" :status="status"
                                            :retryProgressTrigger="retryProgressTrigger">
                                        </JobExecutionProgress>
                                    </div>
                                </div>
                            </div>
                            <transition name="card-pop">
                                <div v-if="pipelineView && nodeInfo?.length > 0 && currentNodeInfo"
                                    class="node-info-wrapper" ref="infoCard">
                                    <div class="card shadow">
                                        <!-- command bar: identity + status on the left, task actions on the right -->
                                        <div class="card-header node-panel-header">
                                            <div class="node-panel-identity">
                                                <span v-if="currentNodeInfo?.status"
                                                    :class="['node-status-dot', statusClassMap[currentNodeInfo?.status?.toLowerCase()], isTaskLive ? 'is-live' : '']"></span>
                                                <h4 class="mb-0 fw-bold text-dark text-truncate">
                                                    {{ currentNodeInfo?.name }}
                                                </h4>
                                                <span v-if="currentNodeInfo?.status"
                                                    :class="['badge', 'text-uppercase', statusBadgeMap[currentNodeInfo?.status?.toLowerCase()] || 'bg-secondary-soft']">
                                                    {{ currentNodeInfo?.status }}
                                                </span>
                                                <button type="button" v-if="currentNodeInfo?.name"
                                                    data-bs-toggle="tooltip" data-bs-placement="top"
                                                    data-bs-title="Open the job behind this task"
                                                    class="btn btn-sm btn-outline-primary border-0 rounded-circle node-icon-btn"
                                                    @click.prevent="redirectToJob(currentNodeInfo)">
                                                    <i class="fa-solid fa-arrow-up-right-from-square small"></i>
                                                </button>
                                            </div>
                                            <div class="node-panel-actions">
                                                <div v-if="hasTaskRequestAccess && currentNodeInfo?.task_id"
                                                    class="dropdown">
                                                    <button type="button"
                                                        class="btn btn-sm btn-primary dropdown-toggle"
                                                        data-bs-toggle="dropdown" aria-expanded="false"
                                                        :disabled="!canCreateTaskRequest" :title="newTaskRequestTooltip">
                                                        <span v-if="creatingTaskRequestType"
                                                            class="spinner-border spinner-border-sm me-1" role="status"
                                                            style="width: 1em; height: 1em;">
                                                            <span class="visually-hidden">Loading...</span>
                                                        </span>
                                                        <i v-else class="fa fa-bolt me-1"></i>
                                                        Task Action
                                                    </button>
                                                    <div class="dropdown-menu dropdown-menu-end node-request-menu">
                                                        <h6 class="dropdown-header">Register a request</h6>
                                                        <a v-for="option in taskRequestOptions" :key="option.type"
                                                            :class="['dropdown-item', 'node-request-option', option.allowed ? '' : 'disabled text-muted']"
                                                            :title="option.tooltip" href="#"
                                                            @click.prevent="option.allowed && createTaskRequest(option.type)">
                                                            <i :class="[option.icon, 'node-request-option-icon']"></i>
                                                            <span class="node-request-option-body">
                                                                <span class="node-request-option-label">{{ option.label
                                                                    }}</span>
                                                                <small class="text-muted">
                                                                    {{ option.allowed ? option.description :
                                                                        option.tooltip }}
                                                                </small>
                                                            </span>
                                                        </a>
                                                        <div class="dropdown-divider"></div>
                                                        <span class="dropdown-item-text node-request-menu-note">
                                                            <i class="fa fa-circle-info me-1"></i>
                                                            Requests are applied when the top level pipeline execution
                                                            is rerun.
                                                        </span>
                                                    </div>
                                                </div>
                                                <button type="button" class="btn btn-sm btn-white node-icon-btn"
                                                    data-bs-toggle="tooltip" data-bs-placement="top"
                                                    data-bs-title="Refresh task requests"
                                                    :disabled="loadingTaskRequests"
                                                    @click="fetchTaskRequests(currentNodeInfo?.task_id)">
                                                    <span v-if="loadingTaskRequests"
                                                        class="spinner-border spinner-border-sm" role="status"
                                                        style="width: 1em; height: 1em;">
                                                        <span class="visually-hidden">Loading...</span>
                                                    </span>
                                                    <i v-else class="fa fa-refresh"></i>
                                                </button>
                                                <button type="button" class="btn-close ms-1"
                                                    @click.prevent="closeNodeInfo()"></button>
                                            </div>
                                        </div>
                                        <div class="card-body node-panel-body">
                                            <div class="node-panel-layout">
                                                <!-- iteration rail, only for loop nodes -->
                                                <div v-if="nodeInfo.length > 1 && isLoopNode(currentNodeInfo)"
                                                    class="node-loop-rail">
                                                    <div class="node-section-label">
                                                        Iterations
                                                        <span class="node-count-pill">{{ nodeInfo.length }}</span>
                                                        <i class="fa fa-circle-info text-muted ms-1"
                                                            :data-bs-title="`List of all the iterations in <strong>${currentNodeInfo?.name}</strong>`"
                                                            data-bs-toggle="tooltip" data-bs-placement="right"
                                                            data-bs-html="true"></i>
                                                    </div>
                                                    <div class="node-loop-list">
                                                        <button type="button" v-for="(task, index) in nodeInfo"
                                                            :key="task.task_id" @click="selectTask(task)"
                                                            :class="['task-item', task.task_id === currentNodeInfo?.task_id ? 'active-task-item' : '']">
                                                            <span
                                                                :class="['node-status-dot', statusClassMap[task.status?.toLowerCase()]]"></span>
                                                            <span class="task-item-index">#{{ index + 1 }}</span>
                                                            <span class="task-item-id text-truncate">{{ task.task_id
                                                                }}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div class="node-panel-main">
                                                    <!-- dense metadata grid, replaces the stacked list rows -->
                                                    <div class="node-meta-grid">
                                                        <div v-if="currentNodeInfo?.task_id" class="node-meta">
                                                            <span class="node-meta-label">
                                                                Task ID
                                                                <i class="fa fa-circle-info"
                                                                    data-bs-title="Task ID" data-bs-toggle="tooltip"
                                                                    data-bs-placement="top"></i>
                                                            </span>
                                                            <span class="node-meta-value node-mono">{{
                                                                currentNodeInfo?.task_id }}</span>
                                                        </div>
                                                        <div v-if="currentNodeInfo?.sandbox_name" class="node-meta">
                                                            <span class="node-meta-label">
                                                                Sandbox
                                                                <i class="fa fa-circle-info" data-bs-toggle="tooltip"
                                                                    data-bs-placement="top"
                                                                    :data-bs-title="'The name of the sandbox where the task\'s job resides'"></i>
                                                            </span>
                                                            <span class="node-meta-value">{{
                                                                currentNodeInfo?.sandbox_name }}</span>
                                                        </div>
                                                        <div v-if="currentNodeInfo?.created_at" class="node-meta">
                                                            <span class="node-meta-label">
                                                                Created
                                                                <i class="fa fa-circle-info"
                                                                    data-bs-title="Timestamp when the task was created"
                                                                    data-bs-toggle="tooltip"
                                                                    data-bs-placement="top"></i>
                                                            </span>
                                                            <span class="node-meta-value">
                                                                {{
                                                                    convertToRelativeTime(dayjs(currentNodeInfo?.created_at))
                                                                }}
                                                                <small class="text-muted d-block">
                                                                    {{ dayjs(currentNodeInfo?.created_at)
                                                                        .format('MMM D, YYYY h:mm:ss A') }}
                                                                </small>
                                                            </span>
                                                        </div>
                                                        <div v-if="currentNodeInfo?.updated_at" class="node-meta">
                                                            <span class="node-meta-label">
                                                                Updated
                                                                <i class="fa fa-circle-info"
                                                                    data-bs-title="The timestamp when the task last was updated"
                                                                    data-bs-toggle="tooltip"
                                                                    data-bs-placement="top"></i>
                                                            </span>
                                                            <span class="node-meta-value">
                                                                {{
                                                                    convertToRelativeTime(dayjs(currentNodeInfo?.updated_at))
                                                                }}
                                                                <small class="text-muted d-block">
                                                                    {{ dayjs(currentNodeInfo?.updated_at)
                                                                        .format('MMM D, YYYY h:mm:ss A') }}
                                                                </small>
                                                            </span>
                                                        </div>
                                                        <div v-if="currentNodeInfo?.full_path"
                                                            class="node-meta node-meta--wide">
                                                            <span class="node-meta-label">
                                                                Full Path
                                                                <i class="fa fa-circle-info" data-bs-toggle="tooltip"
                                                                    data-bs-placement="top"
                                                                    :data-bs-title="'Task full path is a string that uniquely identifies the hierarchical position and sequence of a specific task within the overall pipeline execution'"></i>
                                                            </span>
                                                            <span class="node-meta-value node-mono">{{
                                                                currentNodeInfo?.full_path }}</span>
                                                        </div>
                                                    </div>

                                                    <!-- always visible, whichever tab is open -->
                                                    <div v-if="taskRequestError" class="node-strip node-strip--danger">
                                                        <i class="fa-solid fa-circle-exclamation"></i>
                                                        <span>{{ taskRequestError }}</span>
                                                    </div>
                                                    <div v-if="pendingTaskRequest"
                                                        class="node-strip node-strip--pending">
                                                        <i class="fa fa-hourglass-half"></i>
                                                        <span>
                                                            <strong>{{
                                                                taskRequestTypeLabel(pendingTaskRequest.request_type)
                                                                }}</strong>
                                                            requested &mdash; applied on the next rerun of this pipeline
                                                            execution.
                                                        </span>
                                                        <button type="button"
                                                            class="btn btn-link btn-sm p-0 ms-auto text-nowrap"
                                                            v-if="nodePanelTab !== 'requests'"
                                                            @click="nodePanelTab = 'requests'">
                                                            View request
                                                        </button>
                                                    </div>

                                                    <!-- one table at a time keeps the panel short -->
                                                    <div class="node-panel-tabs" role="tablist">
                                                        <button type="button" role="tab"
                                                            :class="['node-tab', nodePanelTab === 'executions' ? 'active' : '']"
                                                            :aria-selected="nodePanelTab === 'executions'"
                                                            @click="nodePanelTab = 'executions'">
                                                            Executions
                                                            <span class="node-count-pill">{{
                                                                currentNodeInfo?.executions?.length || 0 }}</span>
                                                        </button>
                                                        <button type="button" role="tab"
                                                            :class="['node-tab', nodePanelTab === 'requests' ? 'active' : '']"
                                                            :aria-selected="nodePanelTab === 'requests'"
                                                            @click="nodePanelTab = 'requests'">
                                                            Requests
                                                            <span class="node-count-pill">{{ taskRequests.length
                                                                }}</span>
                                                            <span v-if="pendingTaskRequest"
                                                                class="node-tab-dot"
                                                                data-bs-toggle="tooltip" data-bs-placement="top"
                                                                data-bs-title="A request is waiting to be applied"></span>
                                                        </button>
                                                    </div>

                                                    <!-- v-if, not v-show: the datatable has to mount visible to size its columns -->
                                                    <div v-if="nodePanelTab === 'executions'">
                                                        <div v-if="currentNodeInfo?.executions?.length > 0">
                                                            <datatable
                                                                :tableId="`${currentNodeInfo?.task_id}-executions`"
                                                                :columns="executionsColumns" :searching="false"
                                                                :formatColumn="executionsFormatColumns"
                                                                :data="modifiedExecutionsData" :compact="true"
                                                                :showLoading="false" :ignoreCache="true"
                                                                :detailColumn="{
                                                                    renderBaseUrl: (data) => {
                                                                        const cid = this.$store.state.currentClient?.cid;
                                                                        const taskJobName = currentNodeInfo?.name;
                                                                        const sandboxName = currentNodeInfo?.sandbox_name || sandboxId;
                                                                        const base = this.isDevSandbox
                                                                            ? `/${cid}/amdp/sandbox/${sandboxName}/jobs/${taskJobName}/execution-details/`
                                                                            : `/${cid}/amdp/${sandboxName}/jobs/${taskJobName}/execution-details/`;
                                                                        return base + data.execution_id;
                                                                    },
                                                                }" />
                                                        </div>
                                                        <div v-else class="node-empty">
                                                            <i class="fa fa-inbox"></i>
                                                            <span>No executions found for this task</span>
                                                        </div>
                                                    </div>

                                                    <div v-if="nodePanelTab === 'requests'">
                                                        <div v-if="taskRequests.length"
                                                            class="table-responsive node-table-wrap">
                                                            <table class="table table-sm mb-0 node-request-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Request</th>
                                                                        <th>Request ID</th>
                                                                        <th>State</th>
                                                                        <th>Requested</th>
                                                                        <th class="text-end"></th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr v-for="request in taskRequests"
                                                                        :key="request.request_id"
                                                                        :class="isTaskRequestRealized(request) ? '' : 'is-pending'">
                                                                        <td>
                                                                            <span
                                                                                :class="['badge', request.request_type === 'MARK_SUCCESS' ? 'bg-success-soft' : 'bg-primary-soft']">
                                                                                <i
                                                                                    :class="[request.request_type === 'MARK_SUCCESS' ? 'fa fa-check' : 'fa fa-rotate-right', 'me-1']"></i>
                                                                                {{
                                                                                    taskRequestTypeLabel(request.request_type)
                                                                                }}
                                                                            </span>
                                                                        </td>
                                                                        <td class="node-mono small">
                                                                            {{ request.request_id || '-' }}
                                                                        </td>
                                                                        <td>
                                                                            <template
                                                                                v-if="isTaskRequestRealized(request)">
                                                                                <span class="badge bg-secondary-soft">
                                                                                    <i class="fa fa-check me-1"></i>
                                                                                    Realized
                                                                                </span>
                                                                                <router-link
                                                                                    class="d-block node-mono small mt-1"
                                                                                    :to="realizedExecutionRoute(request.realized_execution_id)"
                                                                                    data-bs-toggle="tooltip"
                                                                                    data-bs-placement="top"
                                                                                    data-bs-title="View the pipeline execution that applied this request">
                                                                                    {{ request.realized_execution_id }}
                                                                                    <i
                                                                                        class="fa-solid fa-arrow-up-right-from-square ms-1"></i>
                                                                                </router-link>
                                                                            </template>
                                                                            <template
                                                                                v-else-if="isMarkSuccessRequest(request)">
                                                                                <span class="badge bg-info-soft">
                                                                                    <i class="fa fa-flag me-1"></i>
                                                                                    Registered
                                                                                </span>
                                                                                <small
                                                                                    class="d-block text-muted mt-1">applies
                                                                                    on next rerun</small>
                                                                            </template>
                                                                            <template v-else>
                                                                                <span class="badge bg-warning-soft">
                                                                                    <i
                                                                                        class="fa fa-hourglass-half me-1"></i>
                                                                                    Pending
                                                                                </span>
                                                                                <small
                                                                                    class="d-block text-muted mt-1">applies
                                                                                    on next rerun</small>
                                                                            </template>
                                                                        </td>
                                                                        <td>
                                                                            <span class="d-block">{{
                                                                                request.created_by || '-' }}</span>
                                                                            <small class="text-muted"
                                                                                v-if="request.created_at">
                                                                                {{ dayjs(request.created_at)
                                                                                    .format('MMM D, YYYY h:mm:ss A') }}
                                                                            </small>
                                                                        </td>
                                                                        <td class="text-end">
                                                                            <button
                                                                                v-if="canCancelTaskRequest(request)"
                                                                                class="btn btn-sm btn-white text-danger node-icon-btn"
                                                                                data-bs-toggle="tooltip"
                                                                                data-bs-placement="left"
                                                                                data-bs-title="Cancel this request. Only requests that are not yet realized can be cancelled"
                                                                                :disabled="taskRequestActionInProgress"
                                                                                @click="deleteTaskRequest(request.request_id)">
                                                                                <span
                                                                                    v-if="deletingTaskRequestId === request.request_id"
                                                                                    class="spinner-border spinner-border-sm"
                                                                                    role="status"
                                                                                    style="width: 1em; height: 1em;">
                                                                                    <span
                                                                                        class="visually-hidden">Loading...</span>
                                                                                </span>
                                                                                <i v-else class="fa fa-trash"></i>
                                                                            </button>
                                                                            <span v-else-if="hasTaskRequestAccess"
                                                                                class="text-muted"
                                                                                data-bs-toggle="tooltip"
                                                                                data-bs-placement="left"
                                                                                :data-bs-title="taskRequestLockTooltip(request)">
                                                                                <i class="fa fa-lock"></i>
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        <div v-else-if="!loadingTaskRequests" class="node-empty">
                                                            <i class="fa fa-bolt"></i>
                                                            <span>No requests registered for this task</span>
                                                            <small v-if="hasTaskRequestAccess" class="text-muted">
                                                                Use <strong>Task Action</strong> above to restart or
                                                                mark this task successful.
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </transition>
                        </template>
                    </tabs>
                </div>
            </TransitionGroup>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc'
import duration from 'dayjs/plugin/duration'
import Datatable from "../../../components/Datatable.vue";
import { secondsToHourMinSec, formatBytes, relativeTime } from '../../../utils/commonFunction';
import Tabs from "../../../components/Tabs.vue";
import JobEditor from '../../../components/sandbox/JobEditor.vue';
import JobExecutionProgress from '../../../components/sandbox/JobExecutionProgress.vue';
import JobExecutorLogs from '../../../components/sandbox/JobExecutorLogs.vue';
import MetricsView from '../../../components/sandbox/Metrics.vue';
import { mapState, mapActions } from "vuex";
import InputField from "../../../components/InputField.vue";
import PipelineGraph from '../../agm/pipelineGraph.vue';
import YAML from 'yaml';
dayjs.extend(duration)
dayjs.extend(utc)

export default {
    components: { Datatable, Tabs, JobEditor, JobExecutionProgress, JobExecutorLogs, InputField, MetricsView, PipelineGraph },
    data() {
        return {
            statusClassMap: {
                "succeeded": "text-success",
                "failed": "text-danger",
                "running": "text-primary",
                "submitted": "text-warning",
                "cancelled": "text-secondary",
            },
            // soft badge variant of statusClassMap, used for the task status pill
            statusBadgeMap: {
                "succeeded": "bg-success-soft",
                "failed": "bg-danger-soft",
                "running": "bg-primary-soft",
                "submitted": "bg-warning-soft",
                "cancelled": "bg-secondary-soft",
            },
            tabItems: [
                {
                    name: "Progress",
                    id: "id1",
                    slot: "progress"
                },
                {
                    name: "Metrics",
                    id: "id3",
                    slot: "metrics"
                },
                {
                    name: "Logs",
                    id: "id2",
                    slot: "logs"
                },
                {
                    name: "Log Streams",
                    id: "id5",
                    slot: "logStreams"

                },
                {
                    name: "Config",
                    id: "id4",
                    slot: "config",
                    hide: true
                },
                {
                    name: "Rerun History",
                    id: "id6",
                    slot: "rerunHistory",
                    hide: true
                }
            ],
            jobObj: this.job,
            name: null,
            status: null,
            statusClass: 'text-primary',
            cluster: null,
            gitCommit: null,
            submittedAt: null,
            submittedRelativeTime: null,
            startedAt: null,
            startedAtRelativeTime: null,
            startedBy: null,
            completedAt: null,
            completedAtRelativeTime: null,
            queueTime: null,
            runtime: null,
            failureReason: null,
            duration: 0,
            tag: null,
            executionId: this.$route.params.executionId,
            // source execution of a rerun chain, task requests are always registered against it
            srcJobExecutionId: null,
            jobName: this.$route.params.jobId,
            sandboxId: this.$route.params.sandboxId,
            logs: "",
            logsError: false,
            breakCheck: false,
            outputPaths: [],
            metrics: null,
            defaultMetrics: null,
            filteredMetrics: null,
            metricColumns: [],
            showAllMetrics: false,
            showDataInUnits: true,
            showMetricsFilterBtn: true,
            yamlContent: {},
            executionConfig: null,
            loadingExecutionConfig: false,
            activeTab: "",
            outputLogLink: null,
            errorLogLink: null,
            outputLog: 0,
            errorLog: 0,
            isDownloading: null,
            metricsErrorMsg: null,
            loading: true,
            executorsArray: [],
            selectedLogType: "logs",
            detailPageOpen: false,
            showCloseBtn: false,
            logStreams: [],
            logStreamColumns: [],
            logStreamFormatColumn: [
                {
                    index: 4,
                    type: "datetime",
                },
            ],
            showLogDataInUnits: true,
            currentLogStream: null,
            currentLogStreamId: null,
            logStreamsData: [],
            viewModalColumn: 1,
            countsV2Metrics: {},
            metricLoading: true,
            timeRange: (this.job && this.job.kind == 'MAPPER-ΜBATCH') ? "overall" : null,
            aggInterval: null,
            customStartTime: null,
            customEndTime: null,
            currentUtcTime: dayjs.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
            count: 1,
            executionStatus: false,
            enableRefreshBtn: false,
            enableShowMoreBtn: false,
            refreshBtnLoading: false,
            showMoreBtnLoading: false,
            getLogStreamsLoading: false,
            retryProgressTrigger: false,
            promotedJobKind: null,

            // rerun history info
            rerunHistory: null,
            rerunHistoryColumns: [
                {
                    data: "execution_id",
                    title: "Execution ID",
                    render: function (data, type, row) {
                        if (row.src_job_execution) {
                            return `${data} <span class="badge" style="background-color: tomato;">Source Execution</span>`;
                        }
                        return data;
                    }
                },
                {
                    data: "status"
                },
                {
                    data: "submitted_at",
                    title: "submitted At"
                },
            ],
            rerunHistoryFormatColumns: [
                {
                    index: 3,
                    type: "datetime"
                }
            ],
            rerunHistoryOrderColumn: [3, "desc"],

            // pipeline view
            pipelineJobsAll: {},
            pipelineView: this.job?.kind?.toLowerCase() === 'pipeline' || false,
            pipelineExecutionDetails: {},
            pipelineBreakCheck: false,
            pipelineTracking: false, // whether the trackPipelineExecutionStatus method is running
            pipelineRunning: false, // whether the pipeline is running
            nodeInfo: null, // node info to display below pipeline
            currentNodeInfo: null, // current node info to display below pipeline
            nodeMetricsInfo: {}, // node metrics to display when hovering the node
            executionsColumns: [
                {
                    data: "execution_id",
                    title: "ID",
                    render: function (data, type, row) {
                        // if tag is rerun, add a layer group icon after the id
                        if (row.tag === "rerun") {
                            return `<span> ${data} <i class="fa fa-layer-group ms-1 text-primary"></i></span>`;
                        }
                        return data;
                    }
                },
                {
                    data: "status"
                },
                {
                    data: "queue_time",
                    title: "Queue Time",
                    render: function (data, type, row) {
                        return secondsToHourMinSec(data);
                    }
                },
                {
                    data: "run_time",
                    title: "Run Time",
                    render: function (data, type, row) {
                        return secondsToHourMinSec(data);
                    }
                },
                {
                    data: "submitted_at",
                    title: "Submitted At",
                },
                {
                    data: "completed_at",
                    title: "Completed At",
                },
            ],
            executionsFormatColumns: [
                {
                    index: 5,
                    type: "datetime"
                },
                {
                    index: 6,
                    type: "datetime"
                }
            ],

            // pipeline execution task requests
            taskRequests: [],
            fetchedTaskRequestsFor: null, // task id the requests in taskRequests belong to
            loadingTaskRequests: false,
            taskRequestError: null,
            creatingTaskRequestType: null, // RERUN | MARK_SUCCESS while the create call is in flight
            deletingTaskRequestId: null,
            taskRequestRoles: [1, 1.5, 3], // only aqfer_admin, aqfer_manager and client_admin can create/cancel task requests
            nodePanelTab: "executions", // executions | requests - only one table is shown at a time

            // pipeline view reset view trigger
            resetViewTrigger: 0,
        }
    },
    props: [
        "cancelJobTrigger",
        "isSandbox",
        "saveJobConfigTrigger",
        "job",
        "isPromotion",
        "promotion",
        "reRunJobTrigger",
        "reRunJobTriggerWithOverride",
        "reRunJobTriggerWithImage",
        "pipelineJobs",
        "override"
    ],
    computed: {
        baseEndpoint() {
            let endpoint = `${this.$store.state.lakeviewUrl}/v1/cids/${this.$store.state.currentClient.cid}/sandboxes/`;
            return endpoint + this.$store.state.sandbox.currentSandbox
        },

        ...mapState({
            jobKinds: (state) => state.amdp.jobKinds
        }),
        logOptions() {
            let options = [];
            if (this.executorsArray.length) {
                options.push({ "label": "Driver Logs", "value": "logs" });
                for (const log of this.executorsArray) {
                    options.push({ "label": log.id, "value": log.id });
                }
            }
            return options;
        },
        isDevSandbox() {
            let devSandboxes = this.$store.state.sandbox.devSandboxes;
            devSandboxes = devSandboxes.filter((sandbox => sandbox.name === this.sandboxId))
            return devSandboxes.length ? true : false;
        },
        pipelineJobConfig() {
            return { ...this.job, ...this.executionConfig };
        },
        // a rerun execution carries the id of the execution it originated from - the task
        // requests apis are always keyed by that source execution, not by the rerun
        taskRequestExecutionId() {
            return this.srcJobExecutionId || this.pipelineExecutionDetails?.src_job_execution_id || this.executionId;
        },
        hasTaskRequestAccess() {
            return this.$store.getters.userRoles.isAccessByRole(this.taskRequestRoles);
        },
        currentTaskStatus() {
            return (this.currentNodeInfo?.status || '').toLowerCase();
        },
        // drives the pulsing status dot in the panel header
        isTaskLive() {
            return ['running', 'submitted'].includes(this.currentTaskStatus);
        },
        // only one not yet realized request can exist for a task at a time
        pendingTaskRequest() {
            return this.taskRequests.find(request => !this.isTaskRequestRealized(request)) || null;
        },
        taskRequestActionInProgress() {
            return !!this.creatingTaskRequestType || !!this.deletingTaskRequestId;
        },
        // RERUN is allowed for succeeded, failed and cancelled tasks
        canRerunTask() {
            return ['succeeded', 'failed', 'cancelled'].includes(this.currentTaskStatus);
        },
        // MARK_SUCCESS is allowed only for failed and cancelled tasks
        canMarkTaskSuccess() {
            return ['failed', 'cancelled'].includes(this.currentTaskStatus);
        },
        taskRequestOptions() {
            return [
                {
                    type: 'RERUN',
                    label: 'Restart',
                    icon: 'fa fa-rotate-right',
                    allowed: this.canRerunTask,
                    description: 'Create a restart request. On the next top level pipeline rerun, this task is executed again with the latest job configuration and parameters',
                    tooltip: this.canRerunTask ? '' : 'Allowed only for a succeeded, failed or cancelled task',
                },
                {
                    type: 'MARK_SUCCESS',
                    label: 'Mark as Successful',
                    icon: 'fa fa-check',
                    allowed: this.canMarkTaskSuccess,
                    description: 'Create a manual success request. On the next top level pipeline rerun, this task is treated as successful without being executed. Execution metrics are not propagated downstream',
                    tooltip: this.canMarkTaskSuccess ? '' : 'Allowed only for a failed or cancelled task',
                },
            ];
        },
        // a task request is only worth registering when the pipeline execution itself ended as
        // failed or cancelled - a succeeded pipeline has nothing to recover, and a still running
        // one cannot be rerun yet
        isPipelineExecutionRecoverable() {
            return ['failed', 'cancelled'].includes((this.status || '').toLowerCase());
        },
        canCreateTaskRequest() {
            if (this.taskRequestActionInProgress) return false;
            if (!this.isPipelineExecutionRecoverable) return false;
            // only one unprocessed request can exist for a task at a time
            if (this.pendingTaskRequest) return false;
            return this.taskRequestOptions.some(option => option.allowed);
        },
        newTaskRequestTooltip() {
            if ((this.status || '').toLowerCase() === 'succeeded') return "The pipeline execution succeeded, there is nothing to recover with a task request";
            if (!this.isPipelineExecutionRecoverable) return "Requests can be registered only after the pipeline execution completes as failed or cancelled";
            if (this.pendingTaskRequest) {
                const label = this.taskRequestTypeLabel(this.pendingTaskRequest.request_type);
                return this.isMarkSuccessRequest(this.pendingTaskRequest)
                    ? `A ${label} request is already registered for this task and is applied on the next rerun`
                    : `A ${label} request is already registered for this task. Cancel it to register a different one`;
            }
            if (!this.taskRequestOptions.some(option => option.allowed)) return "Requests can be submitted only for a succeeded, failed or cancelled task";
            return "Register a request for this task";
        },
        modifiedExecutionsData() {
            // add status flag to the executions data.status column
            return this.currentNodeInfo?.executions?.map(execution => {
                let status = "";
                if (execution.status.toLowerCase() === "succeeded") {
                    status = "<span class='text-success'>●</span> ";
                } else if (execution.status.toLowerCase() === "failed") {
                    status = "<span class='text-danger'>●</span> ";
                } else if (execution.status.toLowerCase() === "running") {
                    status = "<span class='text-primary'>●</span> ";
                } else if (execution.status.toLowerCase() === "submitted") {
                    status = "<span class='text-warning'>●</span> ";
                } else if (execution.status.toLowerCase() === "cancelled") {
                    status = "<span class='text-secondary'>●</span> ";
                }
                execution["status"] = status + execution.status;
                return execution;
            });
        }
    },
    watch: {
        cancelJobTrigger() {
            this.cancelJob();
        },
        showAllMetrics(newVal) {
            if (newVal)
                this.filteredMetrics = this.metrics;
            else
                this.filterInputAndOutMetrics();
        },
        showDataInUnits(newVal) {
            this.convertMetricDataToUnits();
        },
        saveJobConfigTrigger() {
            this.saveJob()
        },
        job(newVal) {
            this.jobObj = newVal;
            if (newVal?.kind?.toLowerCase() === 'pipeline') {
                this.trackPipelineExecutionStatus(this.executionId, true);
                this.pipelineView = true;
            } else {
                this.pipelineView = false;
            }
        },
        async reRunJobTrigger() {
            this.$emit('reRunning', true);
            await this.rerunJob();
            this.$emit('reRunning', false);
        },
        async reRunJobTriggerWithOverride() {
            this.$emit('reRunning', true);
            await this.rerunJob(true);
            this.$emit('reRunning', false);
        },
        reRunJobTriggerWithImage() {
            this.showModal({
                component: "JobReRun",
                data: {
                    jobName: this.jobName,
                    sandboxName: this.sandboxId,
                    executionId: this.executionId,
                    routeNameAfterReRun: this.$store.state.sandbox.devSandboxes.map(sandbox => { return sandbox.name })
                        .includes(this.$store.state.sandbox.currentSandbox) ? "Sandbox Job Execution Details" : "Job Execution Details",
                    overrideCapacity: !!this.override,
                }
            })
        },
        nodeInfo(newValue, oldValue) {
            if (newValue && !oldValue) {
                // Use nextTick to wait for the DOM element to be created by v-if
                this.$nextTick(() => {
                    if (this.$refs.infoCard) {
                        // scrollIntoView is the cleanest modern way to do this
                        this.$refs.infoCard.scrollIntoView({
                            behavior: 'smooth',
                            block: 'end' // Scrolls until the bottom of the card is visible
                        });
                    }
                });
            }
        },
        pipelineJobs(newValue, oldValue) {
            if (newValue) {
                this.fetchExtraJobsInExecution();
            }
        }
    },
    methods: {
        ...mapActions({
            showModal: "showModal",
            cancelSandboxJobExecution: "sandbox/cancelSandboxJobExecution",
            updateSandboxJob: "sandbox/updateSandboxJob",
            readJob: "amdp/readJob",
            reRunSandboxJob: "sandbox/reRunSandboxJob",
            readSandboxJob: "sandbox/readSandboxJob",
            listPipelineTaskRequests: "sandbox/listPipelineTaskRequests",
            createPipelineTaskRequest: "sandbox/createPipelineTaskRequest",
            deletePipelineTaskRequest: "sandbox/deletePipelineTaskRequest",
        }),
        dayjs,
        async rerunJob(overrideCapacity = false) {
            await this.reRunSandboxJob({
                sandboxName: this.sandboxId,
                jobName: this.jobName,
                executionId: this.executionId,
                overrideCapacity: overrideCapacity,
                payload: {},
            }).then((response) => {
                // reroute to the corresponding job execution details page
                const executionId = response.data.execution_id;
                this.$router.push({
                    name: this.$store.state.sandbox.devSandboxes.map(sandbox => { return sandbox.name })
                        .includes(this.$store.state.sandbox.currentSandbox) ? "Sandbox Job Execution Details" : "Job Execution Details",
                    params: {
                        cid: this.$store.state.currentClient.cid,
                        sandboxId: this.sandboxId,
                        jobId: this.jobName,
                        executionId: executionId,
                    },
                });
            }).catch((error) => {
                console.error("Error rerunning job: ", error);
                this.$store.dispatch('addToast', {
                    message: error.response?.data?.error || error.message || "Something went wrong while rerunning the job. Please contact support.",
                    type: 'error'
                });
            });
        },
        async cancelJob() {
            try {
                this.$emit('setRunClick', true);
                const response = await this.cancelSandboxJobExecution({ jobName: this.jobName, executionId: this.executionId });
                this.response = 'Job cancelled successfully.'
            } catch (error) {
                if (error.response && error.response.data && error.response.data.error)
                    self.error = error.response.data.error;
                else self.error = error.message;

                const options = {
                    message: self.error || "Error while cancelling Job",
                    type: "error",
                };
                this.$store.dispatch("addToast", options);
            } finally {
                this.loading = false;
                this.$emit('setRunClick', false);
                this.$emit('showCancelJob', false);
            }

        },
        async handleJobChecking() {
            if (this.breakCheck)
                return;

            const logsPromise = this.getExecutionLogs()
            const executorsPromise = this.getExecutors()
            const statusPromise = await this.getExecutionStatus()

            if (this.count < 10 && (this.logsError || (this.status && this.status != 'failed' && this.status != 'succeeded' && this.status != 'cancelled'))) {
                this.count++;
                await new Promise(resolve => setTimeout(resolve, 2000)); // sleep for 2s
                await this.handleJobChecking();
            } else {
                this.$emit('showCancelJob', false);
                if (this.isPromotion) {
                    this.$emit('getPromotionRunList', true);
                }
            }
        },
        getExecutionStatus() {
            let self = this;
            return axios.get(this.baseEndpoint + `/jobs/${this.jobName}/executions/${this.executionId}`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                },
            }).then((response) => {
                self.loading = false;
                if (response.data) {
                    this.executionStatus = true
                    const runData = response.data;
                    this.status = runData.status.toLowerCase();
                    if (this.status == 'succeeded') {
                        this.statusClass = 'text-success'
                    } else if (this.status == 'failed') {
                        this.statusClass = 'text-danger'
                        this.failureReason = runData.failure_reason
                    } else if (this.status == 'cancelled') {
                        this.statusClass = 'text-danger';
                        this.logs = "No Log files";
                    } else {
                        this.$emit('showCancelJob', true);
                    }


                    this.name = runData.name;
                    this.cluster = runData.cluster;
                    this.srcJobExecutionId = runData.src_job_execution_id || null;

                    this.gitCommit = runData.git_commit ? runData.git_commit.slice(0, 8) : null;
                    this.tag = runData.tag;

                    this.startedBy = runData.created_by
                    if (runData.completed_at) {
                        this.completedAt = runData.completed_at;
                    }

                    if (runData.submitted_at) {
                        this.submittedAt = dayjs(runData.submitted_at);
                        this.submittedRelativeTime = this.convertToRelativeTime(this.submittedAt);
                    }

                    if (runData.started_at) {
                        this.startedAt = runData.started_at;
                        this.startedAtRelativeTime = this.convertToRelativeTime(dayjs(this.startedAt));
                    }
                    if (runData.queue_time) {
                        this.queueTime = runData.queue_time
                    }
                    if (runData.run_time) {
                        this.runtime = runData.run_time
                    }

                    if (this.status == 'succeeded') {
                        this.$store.dispatch("sandbox/fetchSandboxJobRuns", { jobName: this.job.name });
                        this.$emit('hidePromoteBtn', false);
                    }

                    // // Turnoff progress tag based on job status
                    // this.tabItems = this.tabItems.map(function (el) {
                    //     if ((el.slot == "progress") && self.status == "running" && el.hide) {
                    //         self.$store.dispatch('setActiveTab', "Progress");
                    //         el.hide = false;
                    //     } else if ((el.slot == "progress") && self.status != "running" && !el.hide) {
                    //         el.hide = true;
                    //         self.$store.dispatch('setActiveTab', "Metrics")
                    //     }
                    //     return el
                    // });

                    if ((this.status == 'failed' || this.status == 'cancelled') && this.tag != "rerun") {
                        this.$emit('hideReRunJobBtn', false);
                    }

                    if (runData.execution_history) {
                        this.rerunHistory = runData.execution_history.map((execution, index) => {
                            let executionObj = {
                                execution_id: execution.execution_id,
                                status: execution.status,
                                submitted_at: execution.submitted_at,
                                // last execution is assumed to be the source execution
                                src_job_execution: runData.execution_history.length - 1 === index,
                            }

                            let status = "";
                            if (executionObj.status.toLowerCase() === "succeeded") {
                                status = "<span class='text-success'>●</span> ";
                            } else if (executionObj.status.toLowerCase() === "failed") {
                                status = "<span class='text-danger'>●</span> ";
                            } else if (executionObj.status.toLowerCase() === "running") {
                                status = "<span class='text-primary'>●</span> ";
                            } else if (executionObj.status.toLowerCase() === "submitted") {
                                status = "<span class='text-warning'>●</span> ";
                            } else if (executionObj.status.toLowerCase() === "cancelled") {
                                status = "<span class='text-secondary'>●</span> ";
                            }
                            executionObj.status = status + executionObj.status;
                            return executionObj;
                        });

                        // enable rerun history tab
                        this.tabItems = this.tabItems.map(function (el) {
                            if (el.slot == "rerunHistory") {
                                el.hide = false;
                            }
                            return el;
                        });
                    }
                }
            }).catch((error) => {
                if (error.response && error.response.data && error.response.data.error)
                    this.error = error.response.data.error;
                else this.error = error.message;

                if (!this.status) {
                    let toastoptions = {
                        message: "Something went wrong while fetching execution details. Please contact support.",
                        type: "error"
                    }
                    this.$store.dispatch('addToast', toastoptions);
                }
                this.$emit('hideReRunJobBtn', true);
            });
        },
        checkAndGetCountsMetrics() {
            if (this.executionStatus) {
                this.getCountV2Metrics();
                this.executionStatus = false
            }
        },
        getExecutors() {
            return axios.get(this.baseEndpoint + `/jobs/${this.jobName}/executions/${this.executionId}/logs/executors`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`
                }
            }).then((response) => {
                if (response.data)
                    this.executorsArray = response.data.executors || [];
                else
                    this.executorsArray = [];
            }).catch((error) => {
                this.executorsArray = [];
            })

        },
        getLogStreams() {
            this.getLogStreamsLoading = true;
            return axios.get(this.baseEndpoint + `/jobs/${this.jobName}/executions/${this.executionId}/logs/log-streams`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`
                }
            }).then((response) => {
                if (response.data) {
                    this.logStreams = response.data.log_streams || [];
                }
                else {
                    this.logStreams = [];
                }

            }).catch((error) => {
                this.logStreams = [];
            }).finally(() => {
                this.getLogStreamsLoading = false;
            })

        },
        getLogStreamByID(stream) {
            return axios.get(this.baseEndpoint + `/jobs/${this.jobName}/executions/${this.executionId}/logs?source=${stream.source}&log_stream_id=${stream.id}&type=${stream.type}`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`
                }
            }).then((response) => {
                if (response.data)
                    this.logStreamsData = response.data || [];
                else
                    this.logStreamsData = [];
            }).catch((error) => {
                this.logStreamsData = [];
            })

        },
        getExecutionLogs() {
            return axios.get(this.baseEndpoint + `/jobs/${this.jobName}/executions/${this.executionId}/logs`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`
                }
            }).catch((error) => {
                this.logsError = true
                if (error.response.data.error)
                    //don't show pre-job errors
                    if (error.response.data.error.indexOf("the server rejected our request") != -1)
                        this.logs = "";
                    else
                        this.logs = error.response.data.error
                else
                    this.logs = error.message
            }).then((response) => {
                this.logsError = false;
                if (response && response.data) {
                    if (this.logs != response.data.Logs) {
                        this.logs = response.data.Logs

                        const regex = /Copied to S3: WrappedArray\((.*)\)/gi
                        const matches = [...this.logs.matchAll(regex)]
                        let locations = []
                        for (const match of matches) {
                            if (match[1]) {
                                let paths = match[1].split(',').map((path) => {
                                    return 's3://' + path;
                                })
                                locations = locations.concat(paths)
                            }
                        }
                        this.outputPaths = locations

                        //scroll to bottom of logs
                        setTimeout(() => {
                            var logsDiv = document.getElementById("logs");
                            if (logsDiv)
                                logsDiv.scrollTop = logsDiv.scrollHeight;
                        }, 0)
                    }
                    if (response.data.log_files) {
                        let logfiles = response.data.log_files
                        this.outputLogLink = logfiles["stdout.txt"].url;
                        this.outputLog = (logfiles["stdout.txt"].length / (1024 ** 2)).toFixed(2);
                        this.errorLogLink = logfiles["stderr.txt"].url;
                        this.errorLog = (logfiles["stderr.txt"].length / (1024 ** 2)).toFixed(2);
                    }
                } else {
                    this.logs = "No Log files";
                }

            }).finally(() => {
                this.loadMore = false;
            });
        },
        getExecutionConfig() {
            this.loadingExecutionConfig = true;
            return axios.get(this.baseEndpoint + `/jobs/${this.jobName}/executions/${this.executionId}/configs`, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                },
            }).then((response) => {
                if (response.data) {
                    // save the execution config to the executionConfig object
                    this.executionConfig = response.data;

                    let yamlContent = {};
                    const promotionExceutionFlag = this.$route.params.promotionId ? true : false;
                    if (response.data.system_files) {
                        for (const i of response.data.system_files) {
                            if (promotionExceutionFlag && i.relative_path == "vars.yaml") {
                                yamlContent[i.relative_path] = i.content
                            } else if (!promotionExceutionFlag) {
                                yamlContent[i.relative_path] = i.content
                            }

                        }
                    }
                    if (response.data.user_files) {
                        for (const i of response.data.user_files) {
                            if (promotionExceutionFlag && i.relative_path == "vars.yaml") {
                                yamlContent[i.relative_path] = i.content
                            } else if (!promotionExceutionFlag) {
                                yamlContent[i.relative_path] = i.content
                            }
                        }
                    }
                    if (Object.keys(yamlContent).length) {
                        this.tabItems = this.tabItems.map(function (el) {
                            if (el.slot == "config")
                                el.hide = false;

                            return el
                        });
                        this.activeTab = Object.keys(yamlContent)[0];
                        this.yamlContent = JSON.parse(JSON.stringify(yamlContent));

                    }

                }

            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                this.loadingExecutionConfig = false;
            });
        },
        timeRangeChange(timeRange) {
            this.timeRange = timeRange;
        },
        aggIntervalChange(interval) {
            this.aggInterval = interval;
        },
        getCountV2Metrics() {
            let countsPayload = {};

            if (this.timeRange) {
                countsPayload.aggregate = true;
                if (this.timeRange == "last_hour") {
                    const endTime = (this.status === 'running' ? dayjs.utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : this.completedAt)
                    countsPayload.start_time = dayjs
                        .utc(endTime)
                        .startOf("hour")
                        .format("YYYY-MM-DD HH:mm:ss");
                    countsPayload.end_time = dayjs
                        .utc(endTime)
                        .add(1, "hour")
                        .startOf("hour")
                        .format("YYYY-MM-DD HH:mm:ss");

                } else if (this.timeRange == "custom_range") {
                    countsPayload.start_time = dayjs
                        .utc(this.customStartTime)
                        .format("YYYY-MM-DD HH:mm:ss");
                    countsPayload.end_time = dayjs
                        .utc(this.customEndTime)
                        .format("YYYY-MM-DD HH:mm:ss");
                    if (this.aggInterval)
                        countsPayload.time_interval = this.aggInterval;

                }


            }
            countsPayload.filter_dimensions = {
                "cid": this.$store.state.currentClient.cid,
                "sandbox_name": this.sandboxId,
                "job_name": this.jobName,
                "job_run_id": this.executionId
            },
                countsPayload.metrics = this.$store.state.amdp.countsMetricReqObj;


            return axios.post(`${this.$store.state.countsUrl}/v2/cids/${this.$store.state.currentClient.cid}/metrics/batch/query`, countsPayload, {
                headers: {
                    Authorization: `Bearer ${this.$store.state.accessToken}`
                }
            }).then((response) => {
                if (response.data)
                    this.countsV2Metrics = response.data.data || {};
                else
                    this.countsV2Metrics = {};
            }).catch((error) => {
                this.countsV2Metrics = {};
            }).finally(() => {
                this.metricLoading = false;
                if (!Object.keys(this.countsV2Metrics).length) {
                    this.convertMetricDataToUnits();
                }

            })
        },
        filterInputAndOutMetrics() {
            if (this.metrics) {
                this.filteredMetrics = this.metrics.filter((metric) => {
                    return (metric.type == "input" || metric.type == "output")
                });

                if (this.filteredMetrics.length == this.metrics.length)
                    this.showMetricsFilterBtn = false;
                else
                    this.showMetricsFilterBtn = true;
            }
        },
        convertMetricDataToUnits() {
            let self = this;
            this.metricLoading = false;
            this.metrics.map((metric) => {
                for (const key in metric) {
                    const filterMetricColumns = self.metricColumns.filter((column) => { return key == column.data })
                    if (!filterMetricColumns.length) {
                        let tempKey = key
                        if (key == "byte_count") {
                            self.metricColumns.push({
                                data: key,
                                title: tempKey.replace(/-/g, ''),
                                render: function (data, type, row) {
                                    if (!data) return "";
                                    if (!self.showDataInUnits) return data;
                                    return formatBytes(data)
                                },
                            })
                        } else if (key == "record_count") {
                            self.metricColumns.push({
                                data: key,
                                title: tempKey.replace(/-/g, ''),
                                render: function (data, type, row) {
                                    if (!data) return "";
                                    if (!self.showDataInUnits) return data;
                                    return Intl.NumberFormat("en", { notation: "compact", roundingMode: "floor", maximumFractionDigits: 2 }).format(data)
                                },
                            })
                        } else {
                            self.metricColumns.push({
                                data: key,
                                title: tempKey.replace(/-/g, '')
                            })

                        }

                    }
                }
            });

            this.filterInputAndOutMetrics();
        },

        showTab(tab) {
            this.activeTab = tab;
        },
        convertToTime(seconds) {
            return secondsToHourMinSec(seconds);
        },
        convertToRelativeTime(timestamp) {
            return relativeTime(Date.now(), timestamp);
        },
        async downloadFile(url, label) {
            this.isDownloading = true;
            await axios.get(url,
                {
                    responseType: 'blob'
                }).then(response => {

                    var blob = new Blob([response.data], { type: "text/plain" });
                    var url = window.URL.createObjectURL(blob);
                    var a = document.createElement("a");
                    a.href = url;
                    a.download = label;
                    a.click();
                });
            this.isDownloading = null
        },
        async saveJob() {
            this.loading = true;
            let toastoptions;
            try {
                let systemFileKeys = [];
                let userFileKeys = [];
                for (const i of Object.keys(this.yamlContent)) {
                    let isSystemFile = false;
                    if (this.job.system_files) {
                        for (const j of this.job.system_files) {
                            if (j.relative_path == i)
                                isSystemFile = true;
                        }
                    }
                    if (isSystemFile)
                        systemFileKeys.push(i)
                    else
                        userFileKeys.push(i)
                }

                this.jobObj.system_files = systemFileKeys.map((key) => {
                    return { content: (typeof this.yamlContent[key] === 'string') ? this.yamlContent[key] : JSON.stringify(this.yamlContent[key]), relative_path: key };
                });

                this.jobObj.user_files = userFileKeys.map((key) => {
                    return { content: (typeof this.yamlContent[key] === 'string') ? this.yamlContent[key] : JSON.stringify(this.yamlContent[key]), relative_path: key };
                });
                const configPayload = {
                    name: this.jobObj.name,
                    system_files: this.jobObj.system_files,
                    user_files: this.jobObj.user_files,
                };
                const response = await this.updateSandboxJob(configPayload);
                this.$emit('setSnapshot', this.jobObj);
                toastoptions = {
                    message: "Job updated successfully.",
                    type: "success"
                }
                this.$router.push({
                    name: "Sandbox Job Configuration",
                    params: {
                        sandboxId: `${this.$store.state.sandbox.currentSandbox}`,
                        jobId: `${this.jobObj.name}`
                    },
                });
            } catch (error) {
                if (error.response && error.response.data && error.response.data.error)
                    this.error = error.response.data.error;
                else this.error = error.message;

                toastoptions = {
                    message: this.error || "Something went wrong when updating Job. Please contact support.",
                    type: "error"
                }
            } finally {
                this.loading = false;
                this.$store.dispatch('addToast', toastoptions);
            }
        },
        showRowDetail(index) {
            let streamIndex = this.tabItems.findIndex((tabs) => tabs.slot == "logStreams");

            this.currentLogStream = this.logStreams[index];
            this.currentLogStreamId = this.currentLogStream.id;
            if (this.tabItems[streamIndex + 1].slot == 'config') {
                this.tabItems.splice(4, 0, {
                    name: this.currentLogStreamId,
                    id: this.currentLogStreamId,
                    slot: this.currentLogStreamId

                });

            } else {
                this.tabItems.splice(4, 1, {
                    name: this.currentLogStreamId,
                    id: this.currentLogStreamId,
                    slot: this.currentLogStreamId

                });

            }
            this.$store.dispatch('setActiveTab', this.currentLogStreamId);
            this.getLogStreamByID(this.currentLogStream);
        },
        onDetailPageOpenClose(value) {
            this.showCloseBtn = false;
            this.detailPageOpen = value;
        },
        onDetailPageHover(value) {
            this.showCloseBtn = value;
        },
        openModal(data) {
            this.showModal({
                component: "ViewLog", data: {
                    sandboxName: this.$store.state.sandbox.currentSandbox, jobName: this.job.name, executionId: this.executionId,
                    screenlarge: true,
                    status: this.status.match(/\brunning\b/i) ? "running" : "",
                    streams: true,
                    streamData: data,
                    baseEndpoint: this.baseEndpoint
                }
            })
        },
        handleCustomRange({ startTime, endTime }) {
            this.customStartTime = startTime;
            this.customEndTime = endTime;
        },
        async onRefreshClick() {
            this.refreshBtnLoading = true
            this.$emit('hideReRunJobBtn', true);
            if (this.job?.kind?.toLowerCase() === 'pipeline' && !this.pipelineTracking) {
                this.trackPipelineExecutionStatus(this.executionId, true);
            }
            await this.getExecutionStatus();
            this.checkAndGetCountsMetrics();
            this.getExecutionLogs();
            this.getExecutors();
            this.getLogStreams();
            this.getExecutionConfig();
            this.retryProgressTrigger = !this.retryProgressTrigger
            this.refreshBtnLoading = false
        },
        async onShowMoreClick() {
            this.count = 1
            this.showMoreBtnLoading = true
            await this.handleJobChecking()
            this.showMoreBtnLoading = false
        },
        async readDestinationPromotionJob() {
            if (!this.isPromotion || !this.promotion) return;
            const params = {
                sandboxName: this.promotion.dst_sandbox_name,
                jobName: this.promotion.dst_job_name
            }
            await this.readJob(params).then(resp => {
                if (resp.data) {
                    this.promotedJobKind = resp.data.kind
                }
            }).catch(err => {
                console.error(err)
            })
        },
        async trackPipelineExecutionStatus(executionId, getCountsMetrics = false) {
            if (!executionId) {
                console.error('No running execution id found to track pipeline execution status...');
                return;
            }

            this.nodeMetricsInfo = {};
            this.pipelineTracking = true;
            this.pipelineExecutionDetails = {};

            // this loop is to wait till the execution starts running    
            let retryCount = 25;
            try {
                while (retryCount > 0) {
                    const response = await axios.get(
                        `${this.baseEndpoint}/jobs/${this.job?.name}/executions/${executionId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${this.$store.state.accessToken}`,
                            },
                        }
                    );
                    if (response.data?.status != 'SUBMITTED') {
                        break;
                    }
                    await new Promise(resolve => setTimeout(resolve, 5000)); // wait for 5 seconds
                    retryCount--;
                }
            } catch (error) {
                console.error('Error fetching pipeline job execution status: ', error);
            }

            this.pipelineRunning = true;
            try {
                // infinite loop until the component is unmounted or the pipeline is not running
                while (!this.pipelineBreakCheck) {
                    let error = null;
                    let response;
                    let details = {
                        executionDetails: {},
                        executionDetailsByName: {}, // Also maintain a name-keyed bucket (used by in_loop display nodes)
                    };
                    try {
                        response = await this.getPipelineExecutionDetails(this.job?.name, executionId);
                    } catch (e) {
                        error = e.response?.data?.error || e.message;
                        console.error('Error fetching pipeline execution details: ', error);
                    }
                    if (error) break;

                    // parse the response
                    response.data?.tasks?.forEach(task => {
                        const jobName = task.full_path.split('/').pop();
                        // Key by full_path for exact lookup (handles same job name in multiple positions)
                        details.executionDetails[task.full_path] = [task];

                        if (!details.executionDetailsByName[jobName]) details.executionDetailsByName[jobName] = [];
                        details.executionDetailsByName[jobName].push(task);
                    });
                    details = { ...details, ...response.data };
                    this.pipelineExecutionDetails = JSON.parse(JSON.stringify(details));
                    this.fetchExtraJobsInExecution(); // fetch extra jobs that is not in config. Eg, env variables in job configs, can only be fetched by this method.

                    if (getCountsMetrics) {
                        const pipelineJobExecutionDetails = JSON.parse(JSON.stringify(this.pipelineExecutionDetails));
                        // get the node metrics from counts service
                        const nodeMetricsPromises = Object.entries(pipelineJobExecutionDetails?.executionDetails || {}).map(([jobName, tasks]) => {
                            tasks.forEach((task, index) => {
                                // check if task has executions
                                if (!task.executions || !task.executions.length) return null;

                                // do not get metrics for tasks that are not running/completed
                                if (!task.status || ['waiting', 'submitted', 'not_started'].includes(task.status.toLowerCase())) return null;

                                // for each execution of the task, get the metrics
                                task.executions?.forEach(execution => {
                                    let executionId = execution?.execution_id || null;
                                    let jobName = task.full_path.split('/').pop() || null;

                                    // check if executionId and jobName are valid
                                    if (!executionId || !jobName) return null;

                                    // if running - get metrics
                                    // if completed - get metrics if not already fetched or fetch once if last status was running
                                    if (task.status.toLowerCase() === 'running' ||
                                        (['succeeded', 'failed', 'cancelled'].includes(task.status.toLowerCase()) && (!this.nodeMetricsInfo[`${jobName}-${executionId}`] || this.nodeMetricsInfo[`${jobName}-${executionId}`] == 'running'))) {

                                        console.log("getting metrics for job: ", jobName, "execution: ", executionId);
                                        // prepare counts payload
                                        let countsPayload = {
                                            filter_dimensions: {
                                                "cid": this.$store.state.currentClient.cid,
                                                "sandbox_name": this.sandboxId,
                                                "job_name": jobName,
                                                "job_run_id": executionId,
                                                "data_channel": "input"
                                            },
                                            metrics: {
                                                "amdp-metric": JSON.parse(JSON.stringify(this.$store.state.amdp.countsMetricReqObj?.["amdp-metric"] || {}))
                                            }
                                        };
                                        if (countsPayload?.metrics?.["amdp-metric"]) {
                                            countsPayload.metrics["amdp-metric"].header = ["data_channel_type", "record_count"]; // only record count is required as of now
                                        }
                                        return axios.post(`${this.$store.state.countsUrl}/v2/cids/${this.$store.state.currentClient.cid}/metrics/batch/query`, countsPayload, {
                                            headers: {
                                                Authorization: `Bearer ${this.$store.state.accessToken}`,
                                            },
                                        }).then(response => {
                                            if (response.data) {
                                                let resp = response.data;
                                                this.nodeMetricsInfo[`${jobName}-${executionId}`] = task.status.toLowerCase();
                                                if (resp.data?.["amdp-metric"]?.data?.length) {
                                                    let recordCount = resp.data?.["amdp-metric"]?.data?.[0]?.[1] || null; // first appearing record count is taken
                                                    if (!this.nodeMetricsInfo[jobName]) {
                                                        this.nodeMetricsInfo[jobName] = [];
                                                    }
                                                    this.nodeMetricsInfo[jobName].push(recordCount);
                                                } else console.log("No counts data found for job: ", jobName);
                                            }
                                        }).catch(error => {
                                            console.error('Error fetching counts metrics for job: ', jobName, 'execution: ', executionId, 'error: ', error);
                                            return null;
                                        });
                                    }
                                });
                            })
                        });
                        await Promise.allSettled(nodeMetricsPromises);
                    }

                    // now update the node info manually
                    if (this.nodeInfo?.length > 0 && this.currentNodeInfo) {
                        this.onNodeClick(
                            { name: this.currentNodeInfo.name, full_path: this.currentNodeInfo.full_path },
                            this.currentNodeInfo?.task_id || "",
                            false
                        );
                    }

                    // stop if pipeline is not running
                    if (response?.data?.status != 'RUNNING') break;
                    await new Promise(resolve => setTimeout(resolve, 5000)); // wait for 5 seconds
                }
            } catch (error) {
                console.error('Error fetching pipeline execution status: ', error);
            } finally {
                this.pipelineRunning = false;
                this.pipelineTracking = false;
            }
        },
        async getPipelineExecutionDetails(jobName, executionId) {
            return axios.get(
                `${this.baseEndpoint}/pipelines/${jobName}/executions/${executionId}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.$store.state.accessToken}`,
                    },
                }
            ).catch((error) => {
                throw error;
            });
        },
        togglePipelineView(pipelineView) {
            this.pipelineView = pipelineView;
        },
        onNodeClick(nodePayload, taskId = "", refreshTaskRequests = true) {
            // nodePayload may be { name, full_path } (from pipelineGraph) or a plain string (legacy calls)
            const nodeName = typeof nodePayload === 'object' ? nodePayload.name : nodePayload;
            const fullPath = typeof nodePayload === 'object' ? nodePayload.full_path : null;

            let nodeInfo = null;

            if (fullPath && !this.isLoopNode(nodePayload)) {
                // Exact path lookup — one task per position, no collision
                const task = this.pipelineExecutionDetails?.executionDetails?.[fullPath]?.[0] || null;
                nodeInfo = task ? [task] : null;
            }

            // Fallback: name-bucket (YAML path, in_loop display nodes, or legacy string calls)
            if (!nodeInfo) {
                nodeInfo = this.pipelineExecutionDetails?.executionDetailsByName?.[nodeName] || this.pipelineExecutionDetails?.executionDetails?.[nodeName] || null;
            }

            if (!nodeInfo) {
                this.nodeInfo = null;
                this.currentNodeInfo = null;
                this.taskRequests = [];
                this.fetchedTaskRequestsFor = null;
                return;
            }

            // Attach name field to every item
            nodeInfo.forEach(item => { item.name = nodeName; });
            this.nodeInfo = JSON.parse(JSON.stringify(nodeInfo));
            this.currentNodeInfo = taskId
                ? this.nodeInfo.find(item => item.task_id === taskId) || null
                : this.nodeInfo[0];
            this.taskRequestError = null;
            // the status polling loop calls this method to refresh the node info, do not
            // refetch the requests on every poll
            if (refreshTaskRequests) this.fetchTaskRequests(this.currentNodeInfo?.task_id);
        },
        closeNodeInfo() {
            this.nodeInfo = null;
            this.currentNodeInfo = null;
            this.taskRequests = [];
            this.fetchedTaskRequestsFor = null;
            this.taskRequestError = null;
        },
        selectTask(task) {
            this.currentNodeInfo = task;
            this.taskRequestError = null;
            this.fetchTaskRequests(task?.task_id);
        },
        isLoopNode(nodeInfo) {
            // A node is a loop node only when its full_path contains /in_loop/
            return nodeInfo?.full_path?.includes('/in_loop/') || false;
        },
        taskRequestParams(taskId) {
            return {
                sandboxName: this.sandboxId,
                pipelineName: this.job?.name,
                executionId: this.taskRequestExecutionId,
                taskId: taskId,
            };
        },
        taskRequestTypeLabel(requestType) {
            return requestType === 'MARK_SUCCESS' ? 'Mark Success' : 'Restart';
        },
        // A request is realized once the pipeline execution is rerun and it gets picked up -
        // realized_execution_id then holds the execution that applied it. Only requests that
        // are not yet realized can be cancelled, and only one of them can exist for a task.
        isTaskRequestRealized(request) {
            return !!request?.realized_execution_id;
        },
        // a mark success request is never executed, so it never carries a realized execution id
        isMarkSuccessRequest(request) {
            return request?.request_type === 'MARK_SUCCESS';
        },
        // realized requests are already applied and mark success requests cannot be traced back
        // to an execution, neither of them can be cancelled
        canCancelTaskRequest(request) {
            return this.hasTaskRequestAccess && !this.isTaskRequestRealized(request) && !this.isMarkSuccessRequest(request);
        },
        taskRequestLockTooltip(request) {
            return this.isMarkSuccessRequest(request)
                ? "A mark success request cannot be cancelled"
                : "A realized request cannot be cancelled";
        },
        // route to the execution details page of the execution that realized the request
        realizedExecutionRoute(executionId) {
            const cid = this.$store.state.currentClient?.cid;
            const base = this.isDevSandbox
                ? `/${cid}/amdp/sandbox/${this.sandboxId}/jobs/${this.jobName}/execution-details/`
                : `/${cid}/amdp/${this.sandboxId}/jobs/${this.jobName}/execution-details/`;
            return base + executionId;
        },
        async fetchTaskRequests(taskId) {
            if (!taskId || !this.job?.name || !this.taskRequestExecutionId) {
                this.taskRequests = [];
                return;
            }
            if (taskId !== this.fetchedTaskRequestsFor) this.taskRequests = [];
            this.fetchedTaskRequestsFor = taskId;
            this.loadingTaskRequests = true;
            try {
                const response = await this.listPipelineTaskRequests(this.taskRequestParams(taskId));
                // guard against a stale response when the user switched to another task
                if (this.currentNodeInfo?.task_id !== taskId) return;
                this.taskRequests = response.data?.requests || [];
            } catch (error) {
                console.error('Error fetching pipeline execution task requests: ', error);
                if (this.currentNodeInfo?.task_id === taskId) this.taskRequests = [];
            } finally {
                this.loadingTaskRequests = false;
            }
        },
        async createTaskRequest(requestType) {
            const taskId = this.currentNodeInfo?.task_id;
            if (!taskId || !this.canCreateTaskRequest) return;
            if (!this.taskRequestOptions.find(option => option.type === requestType)?.allowed) return;
            this.creatingTaskRequestType = requestType;
            this.taskRequestError = null;
            try {
                await this.createPipelineTaskRequest({ ...this.taskRequestParams(taskId), requestType });
                this.$store.dispatch('addToast', {
                    message: `${this.taskRequestTypeLabel(requestType)} request registered for the task. Rerun the pipeline execution to apply it.`,
                    type: 'success'
                });
                // surface the request that was just registered
                this.nodePanelTab = 'requests';
                await this.fetchTaskRequests(taskId);
            } catch (error) {
                console.error('Error creating pipeline execution task request: ', error);
                this.taskRequestError = error.response?.data?.error || error.message;
            } finally {
                this.creatingTaskRequestType = null;
            }
        },
        async deleteTaskRequest(requestId) {
            const taskId = this.currentNodeInfo?.task_id;
            if (!taskId || !requestId || this.taskRequestActionInProgress) return;
            // a realized request cannot be cancelled
            const request = this.taskRequests.find(item => item.request_id === requestId);
            if (!this.canCancelTaskRequest(request)) return;
            this.deletingTaskRequestId = requestId;
            this.taskRequestError = null;
            try {
                await this.deletePipelineTaskRequest({ ...this.taskRequestParams(taskId), requestId });
                this.$store.dispatch('addToast', {
                    message: "Task request cancelled successfully.",
                    type: 'success'
                });
                await this.fetchTaskRequests(taskId);
            } catch (error) {
                console.error('Error deleting pipeline execution task request: ', error);
                this.taskRequestError = error.response?.data?.error || error.message;
            } finally {
                this.deletingTaskRequestId = null;
            }
        },
        async redirectToJob(currentNodeInfo) {
            if (!currentNodeInfo) return;
            const jobName = currentNodeInfo.name;
            const sandboxName = currentNodeInfo.sandbox_name || this.sandboxId;
            this.$router.push(this.isDevSandbox ? `/${this.$store.state.currentClient.cid}/amdp/sandbox/${sandboxName}/jobs/${jobName}/configuration` :
                `/${this.$store.state.currentClient.cid}/amdp/${sandboxName}/jobs/${jobName}/run-history`
            )
        },
        async fetchExtraJobsInExecution() {
            const pipelineJob = this.pipelineJobConfig || null;
            if (pipelineJob?.kind?.toLowerCase() === "pipeline") {
                if (!this.job.system_files) return null;
                const configFile = pipelineJob.system_files.find(
                    f => f.relative_path === 'config.yaml'
                );
                const varsFile = pipelineJob.system_files.find(
                    f => f.relative_path === 'vars.yaml'
                );
                if (!configFile?.content) return null;

                let jobsInfo = JSON.parse(JSON.stringify({ ...this.pipelineJobs, ...this.pipelineJobsAll }));
                try {
                    // merging is required, since the config file may refer anchors defined in vars file.
                    // yaml parser does not support a case where anchors are used but not defined.
                    const mergedFile = (varsFile?.content || '') + '\n' + (configFile?.content || '');

                    // parseAllDocuments returns an iterable of Document objects
                    const documents = YAML.parseAllDocuments(mergedFile);
                    if (!documents || documents.length === 0) return null;

                    const parsed = documents[documents.length - 1].toJS(); // get the last document parsed since config.yaml file is at last

                    const jobNamesFromYaml = this.getJobNames(parsed);
                    const jobNamesFromTasks = Object.keys(this.pipelineExecutionDetails?.executionDetailsByName || []);
                    const jobNames = [...new Set([...jobNamesFromYaml, ...jobNamesFromTasks])];

                    const readPromises = jobNames.map(async (jobName) => {
                        // if job not in jobsInfo, then fetch job details
                        if (!Object.keys(jobsInfo).includes(jobName)) {
                            return this.readSandboxJob(jobName).then(response => {
                                jobsInfo[jobName] = response.data;
                            });
                        }
                    });
                    const results = await Promise.allSettled(readPromises);
                } catch (e) {
                    console.error('YAML parse error', e);
                } finally {
                    this.pipelineJobsAll = JSON.parse(JSON.stringify(jobsInfo));
                }
            }
        },
        getJobNames(input, names = []) {
            if (input.in_sequence) {
                input.in_sequence.forEach(item => this.getJobNames(item, names));
            } else if (input.in_parallel) {
                input.in_parallel.forEach(item => this.getJobNames(item, names));
            } else if (input.job_name) {
                names.push(input.job_name);
            }
            return names;
        },
    },

    async beforeMount() {
        let self = this;
        this.getExecutionConfig();
        if (this.job?.kind?.toLowerCase() === 'pipeline') {
            this.trackPipelineExecutionStatus(this.executionId, true);
        }
        await this.handleJobChecking();
        this.checkAndGetCountsMetrics();
        this.getLogStreams();
        this.readDestinationPromotionJob();
        this.enableRefreshBtn = true;
        this.enableShowMoreBtn = true;

        this.logStreamColumns = [{ data: "id" },
        { data: "logs" },
        { data: "source", title: "Source" },
        { data: "type", title: "Type" },
        { data: "timestamp", title: "Timestamp" },
        {
            data: "size_in_bytes", title: "Size", render: function (data, type, row) {
                if (!data) return "";
                if (!self.showLogDataInUnits) return data;
                return formatBytes(data)
            }
        }
        ]
        await this.fetchExtraJobsInExecution();
    },

    beforeUnmount() {
        this.breakCheck = true;
        this.pipelineBreakCheck = true;
        this.$emit('setSaveConfigBtn', false);
        this.$emit('hideReRunJobBtn', true);
    },
    mounted() {
        this.$store.dispatch('setActiveTab', "Progress")
        this.$emit('hideEdit', true);
        this.$emit('hideRunJob', true);
        this.$emit('hidePromoteBtn', true);
        this.$emit('setSaveConfigBtn', true);
        this.$emit('hideReRunJobBtn', true);
    },
    emits: ['setRunClick', 'hideEdit', 'hideRunJob', 'showCancelJob', 'hidePromoteBtn', 'setSaveConfigBtn', 'getPromotionRunList', 'hideReRunJobBtn', 'reRunning']
};
</script>



<style scoped>
#logs {
    max-height: 60vh;
    border: 1px;
}

.no-border {
    box-shadow: none;
    border: none !important
}

.downloadLink {
    text-align: right;
}

.custom-refresh-btn {
    font-size: 15px;
    display: inline;
    /* Adjust text size */
}

.small-btn {
    font-size: 0.75rem;
    /* Reduce text size */
    line-height: 1;
    /* Make it compact */
    padding: 4px 6px;
    /* Very small padding */
}

/* Base style for the wrapper to ensure correct positioning context */
.node-info-wrapper {
    margin-top: 1rem;
    will-change: transform, opacity;
    /* Optimization for the animation engine */
}

/* Defining the Entry and Exit Active States (Transition duration and timing) */
.card-pop-enter-active {
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    /* A nice "back out" ease for a snappy pop */
}

.card-pop-leave-active {
    transition: all 0.2s ease-in;
    /* Faster fade out */
}

/* Defining the Start State for Entry / End State for Exit */
.card-pop-enter-from,
.card-pop-leave-to {
    opacity: 0;
    /* Start slightly smaller and lower down */
    transform: scale(0.9) translateY(10px);
}

.job-link-btn:hover {
    text-decoration: underline;
}

/* ------------------------------------------------------------------------- */
/* Task info panel                                                            */
/* ------------------------------------------------------------------------- */

/* Command bar: identity on the left, task actions on the right */
.node-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
    min-height: auto;
    padding-top: 0.875rem;
    padding-bottom: 0.875rem;
}

.node-panel-identity {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    flex: 1 1 auto;
}

.node-panel-actions {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex: none;
}

.node-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.875rem;
    height: 1.875rem;
    padding: 0;
    line-height: 1;
}

.node-status-dot {
    flex: none;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background-color: currentColor;
}

.node-status-dot.is-live {
    animation: node-dot-pulse 1.6s ease-out infinite;
}

@keyframes node-dot-pulse {
    0% {
        box-shadow: 0 0 0 0 currentColor;
        opacity: 1;
    }

    70% {
        box-shadow: 0 0 0 0.375rem transparent;
        opacity: 0.75;
    }

    100% {
        box-shadow: 0 0 0 0 transparent;
        opacity: 1;
    }
}

.node-panel-body {
    max-height: 60vh;
    overflow-y: auto;
    padding-top: 1rem;
}

.node-panel-layout {
    display: flex;
    align-items: flex-start;
    gap: 1.25rem;
}

.node-panel-main {
    flex: 1 1 auto;
    min-width: 0;
}

/* Iteration rail */
.node-loop-rail {
    flex: 0 0 13rem;
    max-width: 13rem;
    min-width: 0;
    border-right: 1px solid var(--bs-border-color);
    padding-right: 1rem;
    align-self: stretch;
}

.node-section-label,
.node-meta-label {
    display: block;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #95aac9;
}

.node-section-label {
    margin-bottom: 0.5rem;
}

.node-loop-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-height: 20rem;
    overflow-y: auto;
}

.task-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.375rem 0.5rem;
    border: 0;
    border-left: 2px solid transparent;
    border-radius: 0.25rem;
    background-color: transparent;
    text-align: left;
    font-size: 0.8125rem;
    color: var(--bs-body-color);
    cursor: pointer;
    transition: background-color 0.12s ease, border-color 0.12s ease;
}

.task-item:hover {
    background-color: rgba(var(--bs-primary-rgb), 0.06);
}

.task-item-index {
    flex: none;
    font-variant-numeric: tabular-nums;
    color: #95aac9;
}

.task-item-id {
    min-width: 0;
    font-family: var(--bs-font-monospace);
    font-size: 0.75rem;
}

.active-task-item {
    background-color: rgba(var(--bs-primary-rgb), 0.1);
    border-left-color: var(--bs-primary);
    color: var(--bs-primary);
    font-weight: 600;
}

/* Dense metadata grid — replaces the stacked full width rows */
.node-meta-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
    gap: 0.75rem 1.5rem;
    padding-bottom: 1rem;
    margin-bottom: 0.875rem;
    border-bottom: 1px solid var(--bs-border-color);
}

.node-meta {
    min-width: 0;
}

.node-meta--wide {
    grid-column: 1 / -1;
}

.node-meta-label i {
    color: #95aac9;
    font-size: 0.6875rem;
}

.node-meta-value {
    display: block;
    margin-top: 0.125rem;
    font-size: 0.8125rem;
    line-height: 1.35;
    word-break: break-word;
}

.node-meta-value small {
    font-size: 0.6875rem;
}

.node-mono {
    font-family: var(--bs-font-monospace);
    font-size: 0.75rem;
}

/* Inline status strips */
.node-strip {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.75rem;
    border-radius: 0.375rem;
    border-left: 3px solid transparent;
    font-size: 0.8125rem;
}

.node-strip--pending {
    border-left-color: var(--bs-warning);
    background-color: rgba(var(--bs-warning-rgb), 0.1);
}

.node-strip--danger {
    border-left-color: var(--bs-danger);
    background-color: rgba(var(--bs-danger-rgb), 0.08);
    color: var(--bs-danger);
}

/* Sub tabs — only one table is visible at a time */
.node-panel-tabs {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-bottom: 0.75rem;
    border-bottom: 1px solid var(--bs-border-color);
}

.node-tab {
    display: inline-flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.4375rem 0.75rem;
    margin-bottom: -1px;
    border: 0;
    border-bottom: 2px solid transparent;
    background: none;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #95aac9;
    cursor: pointer;
    transition: color 0.12s ease, border-color 0.12s ease;
}

.node-tab:hover {
    color: var(--bs-body-color);
}

.node-tab.active {
    color: var(--bs-primary);
    border-bottom-color: var(--bs-primary);
}

.node-count-pill {
    display: inline-block;
    min-width: 1.375rem;
    padding: 0 0.375rem;
    margin-left: 0.375rem;
    border-radius: 0.75rem;
    background-color: rgba(var(--bs-secondary-rgb), 0.12);
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1.25rem;
    text-align: center;
    color: inherit;
}

.node-tab.active .node-count-pill {
    background-color: rgba(var(--bs-primary-rgb), 0.14);
}

.node-tab-dot {
    width: 0.375rem;
    height: 0.375rem;
    margin-left: 0.375rem;
    border-radius: 50%;
    background-color: var(--bs-warning);
}

/* Task Action dropdown */
.node-request-menu {
    min-width: 20rem;
    padding-bottom: 0;
}

.node-request-option {
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    padding: 0.5rem 1rem;
    white-space: normal;
}

.node-request-option-icon {
    width: 1rem;
    margin-top: 0.1875rem;
    text-align: center;
}

.node-request-option-body {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.node-request-option-label {
    font-weight: 600;
    font-size: 0.8125rem;
}

.node-request-option-body small {
    font-size: 0.6875rem;
    line-height: 1.3;
}

.node-request-menu-note {
    display: block;
    padding: 0.5rem 1rem;
    font-size: 0.6875rem;
    line-height: 1.3;
    color: #95aac9;
    white-space: normal;
}

/* Requests table */
.node-table-wrap {
    border: 1px solid var(--bs-border-color);
    border-radius: 0.375rem;
    overflow: hidden;
}

.node-request-table th {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    font-size: 0.6875rem;
    letter-spacing: 0.06em;
}

.node-request-table td {
    padding-top: 0.625rem;
    padding-bottom: 0.625rem;
    font-size: 0.8125rem;
    vertical-align: middle;
}

.node-request-table tr.is-pending td {
    background-color: rgba(var(--bs-warning-rgb), 0.04);
}

.node-request-table tr:last-child td {
    border-bottom: 0;
}

/* Empty states */
.node-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding: 1.75rem 1rem;
    border: 1px dashed var(--bs-border-color);
    border-radius: 0.375rem;
    color: #95aac9;
    font-size: 0.8125rem;
    text-align: center;
}

.node-empty i {
    font-size: 1.125rem;
    margin-bottom: 0.25rem;
    opacity: 0.6;
}

@media (max-width: 767.98px) {
    .node-panel-layout {
        flex-direction: column;
    }

    .node-loop-rail {
        flex: 1 1 auto;
        max-width: none;
        width: 100%;
        border-right: 0;
        border-bottom: 1px solid var(--bs-border-color);
        padding-right: 0;
        padding-bottom: 0.75rem;
    }
}
</style>

<style lang="scss">
@use '../../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .active-task-item {
        background-color: #002a65 !important;
        font-weight: bold !important;
    }

    // the soft tints in the task info panel need a little more presence on a dark card
    .node-strip--pending {
        background-color: rgba(var(--bs-warning-rgb), 0.18) !important;
    }

    .node-strip--danger {
        background-color: rgba(var(--bs-danger-rgb), 0.18) !important;
    }

    .node-request-table tr.is-pending td {
        background-color: rgba(var(--bs-warning-rgb), 0.08) !important;
    }
}
</style>